import type Graph from 'graphology';

export type FileTreeKind = 'Directory' | 'File';

export interface FileTreeNode {
  readonly id: string;
  readonly name: string;
  readonly kind: FileTreeKind;
  readonly path: string;
  readonly children: FileTreeNode[];
}

interface MutableNode {
  id: string;
  name: string;
  kind: FileTreeKind;
  path: string;
  children: MutableNode[];
}

const TREE_KINDS: ReadonlySet<FileTreeKind> = new Set(['Directory', 'File']);

/**
 * Walk CONTAINS edges between Directory/File nodes, returning roots (nodes with no
 * incoming CONTAINS from another tree node). Children: directories first, then alphabetic.
 */
export function buildFileTree(graph: Graph): FileTreeNode[] {
  const nodes = new Map<string, MutableNode>();
  const incomingTreeEdges = new Map<string, number>();

  graph.forEachNode((id, attrs) => {
    const kind = attrs.kind as string | undefined;
    if (!kind || !TREE_KINDS.has(kind as FileTreeKind)) return;
    const name = (attrs.filePath as string | undefined) ?? (attrs.label as string) ?? id;
    nodes.set(id, {
      id,
      name: lastSegment(name),
      kind: kind as FileTreeKind,
      path: name,
      children: [],
    });
    incomingTreeEdges.set(id, 0);
  });

  graph.forEachEdge((_edgeKey, attrs, source, target) => {
    if (attrs.relationType !== 'CONTAINS') return;
    const parent = nodes.get(source);
    const child = nodes.get(target);
    if (!parent || !child) return;
    parent.children.push(child);
    incomingTreeEdges.set(target, (incomingTreeEdges.get(target) ?? 0) + 1);
  });

  const roots: MutableNode[] = [];
  for (const [id, count] of incomingTreeEdges) {
    if (count === 0) {
      const n = nodes.get(id);
      if (n) roots.push(n);
    }
  }

  const sorted = roots.map(sortRecursive);
  sorted.sort(compareNodes);
  return sorted;
}

function sortRecursive(n: MutableNode): MutableNode {
  n.children = n.children.map(sortRecursive).sort(compareNodes);
  return n;
}

function compareNodes(a: MutableNode, b: MutableNode): number {
  if (a.kind !== b.kind) return a.kind === 'Directory' ? -1 : 1;
  return a.name.localeCompare(b.name);
}

function lastSegment(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx === -1 ? trimmed : trimmed.slice(idx + 1);
}
